import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, lastValueFrom, timeout } from 'rxjs';
import { getQueryParam } from '../../core/initial-search-params';
import type { ClubCommentRequest } from '../../models/club-comment';
import type {
  LocatorLike,
  MetadataResponse,
  MetadataUserDataSettingsResponse,
} from '../../models/metadata-response';
import type { PaymentStatusResponse } from '../../models/payment-status';
import type {
  AnnotationMaterialResponse,
  PronunciationSpeech,
  AnnotationMaterialSave,
  QuestionAnswerResponse,
  QuestionAnswerSave,
  QuestionMaterialResponse,
  QuestionMaterialSave,
  QuizAnswerResponse,
  QuizAnswerSave,
  QuizMaterialResponse,
  QuizMaterialSave,
  QuizQuestionAiResponse,
  QuizQuestionAiSave,
  VoiceAnswerResponse,
  VoiceAnswerSave,
  VoiceMaterialResponse,
  VoiceMaterialSave,
} from '../../models/quiz';
import type {
  FinishReadingRequest,
  StartReadingRequest,
  StartReadingResponse,
} from '../../models/reading-stats';
import type { SelectionResponse, SelectionSave, TextFoundResponse } from '../../models/selections';
import type {
  TextInfoRequest,
  TextInfoResponse,
  TranslateRequest,
  Translation,
} from '../../models/text-info';

@Injectable({
  providedIn: 'root',
})
export class ReaderApi {
  private static readonly API_BASE_URL = '/nubereader';
  private static readonly PAYMENT_STATUS_PATH = '/paymentStatus';
  private static readonly PAYMENT_STATUS_TIMEOUT = 8000;
  private static readonly BOOK_CLUB_PARAMS = ['bc', 'c', 'b', 'mc'] as const;
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  private getBookClubParams(): HttpParams {
    const getParam = (key: string): string | null => getQueryParam(this.route.snapshot, key);
    if (!getParam('bc')) return new HttpParams();
    return ReaderApi.BOOK_CLUB_PARAMS.reduce((acc, key) => {
      const value = getParam(key);
      return value ? acc.set(key, value) : acc;
    }, new HttpParams());
  }

  private static buildReadingMaterialFormData(
    payload: QuizMaterialSave | QuestionMaterialSave | AnnotationMaterialSave | VoiceMaterialSave,
    images: File[],
  ): FormData {
    const formData = new FormData();
    formData.append('exercise', JSON.stringify(payload));
    images.forEach((image) => {
      formData.append('images', image);
    });
    return formData;
  }

  private static extensionForAudio(mimeType: string): string {
    if (mimeType.includes('wav')) return 'wav';
    if (mimeType.includes('mp4')) return 'm4a';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  }

  async getMetadataResource(token: string): Promise<MetadataResponse> {
    return lastValueFrom(
      this.http.get<MetadataResponse>(ReaderApi.API_BASE_URL, {
        headers: { token },
        credentials: 'omit',
        params: this.getBookClubParams(),
      }),
    );
  }

  async getSelectionsResource(token: string): Promise<SelectionResponse[]> {
    return lastValueFrom(
      this.http.get<SelectionResponse[]>(`${ReaderApi.API_BASE_URL}/selections`, {
        headers: { token },
      }),
    );
  }

  async searchText(text: string): Promise<TextFoundResponse[]> {
    if (!text.trim()) return [];
    return firstValueFrom(
      this.http.get<TextFoundResponse[]>('/nubereader/search', {
        params: { text },
      }),
    );
  }

  async saveSelection(selectionPayload: SelectionSave): Promise<SelectionResponse> {
    return firstValueFrom(
      this.http.post<SelectionResponse>('/nubereader/selections', selectionPayload),
    );
  }

  async updateSelection(
    selectionId: string,
    selectionPayload: SelectionSave,
  ): Promise<SelectionResponse> {
    return firstValueFrom(
      this.http.put<SelectionResponse>(`/nubereader/selections/${selectionId}`, selectionPayload),
    );
  }

  async removeSelection(selectionId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/nubereader/selections/${selectionId}`));
  }

  async saveLastLocation(savedPlace: {
    locator: LocatorLike;
    idref: string;
    contextReadingPoint: string;
    progress: number;
  }): Promise<object> {
    return firstValueFrom(this.http.post('/nubereader/saveLastPageRead', { savedPlace }));
  }

  async startReading(body: StartReadingRequest): Promise<StartReadingResponse> {
    return firstValueFrom(this.http.post<StartReadingResponse>('/nubereader/statistics', body));
  }

  async finishReading(readingId: string, body: FinishReadingRequest): Promise<void> {
    await firstValueFrom(
      this.http.put(`/nubereader/statistics/${readingId}`, body, { responseType: 'text' }),
    );
  }

  async saveUserSettings(preferences: MetadataUserDataSettingsResponse): Promise<void> {
    await firstValueFrom(
      this.http.put(
        '/nubereader/booksUsersData',
        { settings: preferences },
        { responseType: 'text' },
      ),
    );
  }

  async saveNotShowAlertMessages(notShowAlertMessages: string[]): Promise<void> {
    await firstValueFrom(
      this.http.put(
        '/nubereader/booksUsersData',
        { notShowAlertMessages },
        { responseType: 'text' },
      ),
    );
  }

  /** Tells the server the book is (no longer) stored for offline reading in some browser. */
  async setDownloaded(downloaded: boolean): Promise<void> {
    await firstValueFrom(
      this.http.put('/nubereader/booksUsersData', { downloaded }, { responseType: 'text' }),
    );
  }

  async getCoverUrl(bookUserDataId: string): Promise<string> {
    const { bookCoverUrl } = await firstValueFrom(
      this.http.get<{ bookCoverUrl: string }>(
        `${ReaderApi.API_BASE_URL}/bookUserData/${encodeURIComponent(bookUserDataId)}/cover`,
      ),
    );
    return bookCoverUrl;
  }

  async getTextInfo(body: TextInfoRequest): Promise<TextInfoResponse> {
    return firstValueFrom(this.http.post<TextInfoResponse>('/nubereader/wiki', body));
  }

  async getTextTranslation(body: TranslateRequest): Promise<Translation> {
    return firstValueFrom(this.http.post<Translation>('/nubereader/translate', body));
  }

  async updateUserLanguage(language: string): Promise<void> {
    await firstValueFrom(this.http.put('/nubereader/userLanguage', { language }));
  }

  async getPaymentStatus(): Promise<PaymentStatusResponse> {
    return firstValueFrom(
      this.http
        .get<PaymentStatusResponse>(`${ReaderApi.API_BASE_URL}${ReaderApi.PAYMENT_STATUS_PATH}`)
        .pipe(timeout(ReaderApi.PAYMENT_STATUS_TIMEOUT)),
    );
  }

  async sendClubComment(body: ClubCommentRequest): Promise<void> {
    await firstValueFrom(
      this.http.post('/nubereader/comment', body, { params: this.getBookClubParams() }),
    );
  }

  async createQuiz(payload: QuizMaterialSave, images: File[]): Promise<QuizMaterialResponse> {
    return firstValueFrom(
      this.http.post<QuizMaterialResponse>(
        '/nubereader/readingMaterial',
        ReaderApi.buildReadingMaterialFormData(payload, images),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async updateQuiz(
    quizId: string,
    payload: QuizMaterialSave,
    images: File[],
  ): Promise<QuizMaterialResponse> {
    return firstValueFrom(
      this.http.put<QuizMaterialResponse>(
        `/nubereader/readingMaterial/${quizId}`,
        ReaderApi.buildReadingMaterialFormData(payload, images),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async deleteQuiz(quizId: string, relId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`/nubereader/readingMaterial/${quizId}`, {
        params: new HttpParams().set('relId', relId),
      }),
    );
  }

  async saveQuizAnswer(payload: QuizAnswerSave): Promise<QuizAnswerResponse> {
    return firstValueFrom(
      this.http.post<QuizAnswerResponse>('/nubereader/readingMaterial/answers', payload, {
        params: new HttpParams().set('relId', payload.relId),
      }),
    );
  }

  async finishQuizAnswer(answerId: string, relId: string): Promise<QuizAnswerResponse> {
    return firstValueFrom(
      this.http.post<QuizAnswerResponse>(
        `/nubereader/readingMaterial/answers/finish/${answerId}`,
        {},
        { params: new HttpParams().set('relId', relId) },
      ),
    );
  }

  async createQuestion(
    payload: QuestionMaterialSave,
    images: File[],
  ): Promise<QuestionMaterialResponse> {
    return firstValueFrom(
      this.http.post<QuestionMaterialResponse>(
        '/nubereader/readingMaterial',
        ReaderApi.buildReadingMaterialFormData(payload, images),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async updateQuestion(
    questionId: string,
    payload: QuestionMaterialSave,
    images: File[],
  ): Promise<QuestionMaterialResponse> {
    return firstValueFrom(
      this.http.put<QuestionMaterialResponse>(
        `/nubereader/readingMaterial/${questionId}`,
        ReaderApi.buildReadingMaterialFormData(payload, images),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async saveQuestionAnswer(payload: QuestionAnswerSave): Promise<QuestionAnswerResponse> {
    return firstValueFrom(
      this.http.post<QuestionAnswerResponse>('/nubereader/readingMaterial/answers', payload, {
        params: new HttpParams().set('relId', payload.relId),
      }),
    );
  }

  async createAnnotation(payload: AnnotationMaterialSave): Promise<AnnotationMaterialResponse> {
    return firstValueFrom(
      this.http.post<AnnotationMaterialResponse>(
        '/nubereader/readingMaterial',
        ReaderApi.buildReadingMaterialFormData(payload, []),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async updateAnnotation(
    annotationId: string,
    payload: AnnotationMaterialSave,
  ): Promise<AnnotationMaterialResponse> {
    return firstValueFrom(
      this.http.put<AnnotationMaterialResponse>(
        `/nubereader/readingMaterial/${annotationId}`,
        ReaderApi.buildReadingMaterialFormData(payload, []),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async createVoiceQuestion(payload: VoiceMaterialSave): Promise<VoiceMaterialResponse> {
    return firstValueFrom(
      this.http.post<VoiceMaterialResponse>(
        '/nubereader/readingMaterial',
        ReaderApi.buildReadingMaterialFormData(payload, []),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async updateVoiceQuestion(
    voiceId: string,
    payload: VoiceMaterialSave,
  ): Promise<VoiceMaterialResponse> {
    return firstValueFrom(
      this.http.put<VoiceMaterialResponse>(
        `/nubereader/readingMaterial/${voiceId}`,
        ReaderApi.buildReadingMaterialFormData(payload, []),
        { params: new HttpParams().set('relId', payload.relId) },
      ),
    );
  }

  async saveVoiceAnswer(payload: VoiceAnswerSave, audio: Blob): Promise<VoiceAnswerResponse> {
    const formData = new FormData();
    formData.append('answer', JSON.stringify(payload));
    formData.append(
      'voice',
      audio,
      `${payload.question}.${ReaderApi.extensionForAudio(audio.type)}`,
    );
    return firstValueFrom(
      this.http.post<VoiceAnswerResponse>('/nubereader/readingMaterial/answers', formData, {
        params: new HttpParams().set('relId', payload.relId),
      }),
    );
  }

  /**
   * The server only synthesises the sentence of the voice question or one of its words, in the
   * language of the question or, when it has none, the one given here.
   */
  async getPronunciationSpeech(request: {
    relId: string;
    questionId: string;
    text: string;
    language?: string;
  }): Promise<PronunciationSpeech> {
    const { relId, questionId, text, language } = request;
    let params = new HttpParams()
      .set('relId', relId)
      .set('questionId', questionId)
      .set('text', text);
    if (language) params = params.set('language', language);
    return firstValueFrom(
      this.http.get<PronunciationSpeech>(`${ReaderApi.API_BASE_URL}/pronunciation/speech`, {
        params,
      }),
    );
  }

  async getEmbeddedContent(url: string): Promise<{ html: string; type: string } | undefined> {
    try {
      return await firstValueFrom(
        this.http.get<{ html: string; type: string }>('/nubereader/embedded', {
          params: new HttpParams().set('urlIframe', url),
        }),
      );
    } catch {
      return undefined;
    }
  }

  async generateQuizQuestionsWithAi(
    payload: QuizQuestionAiSave,
  ): Promise<QuizQuestionAiResponse[]> {
    return firstValueFrom(
      this.http.post<QuizQuestionAiResponse[]>(
        '/nubereader/ole-ia/generate-quiz-questions',
        payload,
      ),
    );
  }
}
